/**
 * @file     worker/management.js - support code for remote (portal) management of DCP Workers.
 * @author   Wes Garland, wes@distributive.network
 * @date     Nov 2024
 *
 * @notes
 * Part of worker management is managing the credentials that workers have access to. This gets tricky
 * in large installations, because it is reasonable to expect that a single owner (portal account) might
 * need to manage workers that aren't allowed to know each other's credentials; for example, in a multi-
 * department campus with a shared infrastructure team.
 *
 * Credentials accessible to workers are accessed via the workerCreds table. This table and its friends
 * are exposed for read-only use by the worker with a w$ prefix in Jnu.  To solve the problem described
 * above, we use a series of cookies and always() clauses in the RLS security policies for these tables,
 * allowing the workers to "prove" that they are allowed to read the given rows.
 *
 *   worker.owner + workerCookie => many workerCreds rows, each has credCookie
 *   each workerCreds            => one credsList rows
 *   credsList.id + credCookie   => many credentials
 *   each credential             => joinKey + joinSecret (etc)
 */
'use strict';
const identity = require('dcp/dcp-client/identity');
const jnu      = require('dcp/dcp-client/jnu');
const wallet   = require('dcp/dcp-client/wallet');
const dcpEnv   = require('dcp/common/dcp-env');

const { randStr21 }        = require('dcp/protocol/utils');
const { localStorage }     = require('dcp/common/dcp-localstorage');
const { localStorageKeys } = require('../consts');
const { generateWorkerId,
        submitSignedPost } = require('dcp/utils');

exports.register             = register;
exports.obtainWorkerId       = obtainWorkerId;
exports.resetLocalStorage    = resetLocalStorage;
exports.jnuConnect           = jnuConnect;

Object.assign(exports, require('./worker-config'));
Object.assign(exports, require('./worker-data'));
Object.assign(exports, require('./unclaimed-earnings'));

/**
 * Register a DCP Worker with the scheduler.
 * This code works to either re-assign a previously-registered worker, or a never-registered worker.
 *
 * The registrationKey is a private key in PrivateKey or Keystore format. It is a proxy key which grants
 * the bearer access to register new workers, until <uses> have been used up or the expiry date is
 * reached. The operation is pxAuth.worker.register.
 *
 * Messages are sent to althea via the register-worker operation, signed by the registrationKey. Inside
 * each message is another message, called 'signedDetails'. This message is signed with the worker's
 * identity, which is usually stored in localStorage or ~/.dcp/id.keystore. Both signatures together
 * tell althea that the registration is properly authorized.
 *
 * Applications implementing worker registration features are responsible for restarting (or stopping)
 * versions of themselves that are already running with the same worker id after registration has
 * completed.
 *
 * Whenever a worker starts (or restarts), it looks in local storage for a bank account that was created
 * to hold "unclaimed credits". This is the default place where credits go.  If there are credits
 * there and a different payment account is used, the credits will be transfered to the earnings
 * account.  This is how users get their funds after registration; being registered gives the workers
 * access to the workerConfigValue database table which holds a paymentAddress node. If there is a race
 * condition that causes credits to be accured in the local storage bank account after registration, a
 * restart should make the worker-owner whole, provided the local storage hasn't been reset.
 *
 * @param {DistributiveWorker}  worker           the instance of worker to register, used to access
 *                                               worker.id, worker.config, etc.
 * @param {Keystore|PrivateKey} registrationKey  (a proxy of) the owner's identity. The proxied identity
 *                                               needs pxAuth.worker.register access.
 * @param {string}              label            an optional label identifying the worker to the owner
 * @param {number}              flags            flags for the worker table, see ../consts workerFlags.
 */
async function register(worker, registrationKey, label, flags)
{
  const osBasic = require('dcp/common/os-basic');
  if (worker.config.unmanaged === true)
    throw new Error('cannot register unmanaged worker');

  if (!(registrationKey instanceof wallet.PrivateKey))
    registrationKey = new wallet.PrivateKey(registrationKey);

  const messageId = registrationKey.address + '-' + randStr21(); /* random string to tie inner/outer messages together */
  const details = {
    messageId,
    workerId: worker.id,
    flags,
  };

  if (dcpEnv.isBrowserPlatform)
    details.label = label || window.location.origin;
  else
    details.label = label || `${osBasic.username()}@${osBasic.hostname()}`;

  /* The outer message is signed by the registrationKey, which should be a proxy for the worker-owner
   * with pxAuth.worker.registation permissions. The inner message is signed by the worker itself,
   * which indicates that we truly have the right to transfer worker ownership.
   */
  const signedDetails = await identity.get().makeSignedMessage(details);
  const endpoint = dcpConfig.pxAuth.location.resolve('register-worker');
  const expiry = Math.floor(Date.now()/1000) + 3600;
  const messageBody = Object.assign({ messageId, signedDetails }, { endpoint, expiry });

  const result = await submitSignedPost(registrationKey, messageBody);
  return result;
}

function obtainWorkerId()
{
  const { DistributiveWorker } = require('..');
  if (DistributiveWorker.obtainWorkerId !== obtainWorkerId) /* static method is official monkey-patch point */
    return DistributiveWorker.obtainWorkerId();

  var workerId = localStorage.getItem(localStorageKeys.worker.id);
  if (!workerId)
  {
    /* localStorage workerId key is deprecated; remove Dec 2025 /wg */
    workerId = localStorage.getItem('workerId') || generateWorkerId();
    localStorage.setItem(localStorageKeys.worker.id, workerId);
  }

  return workerId;
}

/** XXX @todo bank balance */
function resetLocalStorage()
{
  for (let key of [ 'workerId', 'disable_worker' ]) /* obsolete keys */
    localStorage.removeItem(key);
  for (let key of Object.values(localStorageKeys.worker))
    localStorage.removeItem(key);
}

async function jnuConnect()
{
  /* eslint-disable require-atomic-updates */
  const currentIdentity = identity.get();
  if (!currentIdentity.address.eq(jnuConnect.__address))
  {
    const connection = await jnu.connect('scheduler', currentIdentity);
    jnuConnect.__address = currentIdentity.address;
    jnuConnect.__connection = connection;
  }
  return jnuConnect.__connection;
  /* eslint-enable require-atomic-updates */
}
